/**
 * ContentPlannerPage - Unified Content Planning Hub
 *
 * Content Planner is the final surface. The table/list remain planner-native
 * while carrying over the operational workflows from the Blog Posts queue.
 */

import React, {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { flushSync } from 'react-dom';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Modal } from 'antd';
import type { MenuProps } from 'antd';
import { toast } from 'sonner';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Eye,
  FileText,
  Link2,
  RefreshCw,
  RotateCcw,
  Search,
  Trash2,
  UploadCloud,
} from 'lucide-react';
import { ContentCalendarView } from '@/components/content-planner/calendar/ContentCalendarView';
import { ContentTrackerTable } from '@/components/content-planner/table/ContentTrackerTable';
import { AgentSurfaceIntro } from '@/webmcp/ui/AgentSurfaceIntro';
import { AgentActivityDock } from '@/webmcp/ui/AgentActivityDock';
import { useAgentActivity } from '@/webmcp/agentActivity';
import { ContentListView } from '@/components/content-planner/list';
import { ContentCreationWizard } from '@/components/content-planner/ContentCreationWizard';
import { canAccessInternalAdsTools } from '@/lib/adsToolsAccess';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';
import { useAdsViewStore } from '@/stores/useAdsViewStore';
import { KeywordFeedPanel } from '@/components/content-planner/KeywordFeedPanel';
import { ScheduleModal } from '@/components/content-planner/schedule/ScheduleModal';
import { PageHeader, FilterBar, SummaryStrip } from '@/components/content-planner/chrome';
import type { ViewMode } from '@/components/content-planner/chrome';
import {
  BulkActionBar,
  getBulkActionBuckets,
  groupCalendarItemsByTenant,
  isCompleted,
  isProcessing,
  isPublished,
  isScheduled,
  tenantForCalendarItem,
  type RowPrimaryAction,
} from '@/components/content-planner/shared';
import {
  useMultiWorkspaceCalendar,
  contentPlannerKeys,
} from '@/hooks/useContentPlannerAPI';
import { useAutoPublishManager } from '@/hooks/useAutoPublishManager';
import { useTenantStore } from '@/stores/useTenantStore';
import { apiClient } from '@/services/apiClient';
import { blogCreationService } from '@/services/blogCreationService';
import { buildGateItem, confirmPublish, isPublishCancelledError } from '@/lib/prePublishGate';
import {
  draftContentPlannerItems,
  publishContentPlannerItems,
} from '@/services/contentPlannerBulkActions';
import { generateHTMLFromRTE } from '@/components/blog-templates';
import type { ContentDetail } from '@/components/BlogCreationCommandCenter/types';
import { useQueryClient } from '@tanstack/react-query';
import type { CalendarItem } from '@/types/contentPlanner';
import { universalBlogEditorPath } from '@/utils/editorRoutes';

const VIEW_MODES: ViewMode[] = ['list', 'table', 'calendar', 'feed'];
const PAGE_SIZE = 25;

function isViewMode(value: string | null): value is ViewMode {
  return value !== null && (VIEW_MODES as string[]).includes(value);
}

function titleFor(item: CalendarItem): string {
  return item.title || item.keyword || 'Untitled article';
}

function encodeTenant(tenantId: string): string {
  return encodeURIComponent(tenantId);
}

/** Hidden generation policy for row-level Generate. */
const REGENERATE_WRITER_MODEL = 'qwen3.8-flash';

function editorPath(contentId: string): string {
  return universalBlogEditorPath(contentId);
}

// Actions promoted to prominent inline buttons (see getRowPrimaryActions) are
// removed from the ⋯ overflow menu so they aren't duplicated.
const PROMOTED_INLINE_KEYS = new Set(['publish', 'sync', 'draft', 'retry', 'delete']);

/** Drop promoted keys from the kebab menu and normalize orphaned dividers. */
function pruneMenu(items: MenuProps['items']): MenuProps['items'] {
  if (!items) return items;
  const kept = items.filter(
    (it) => !!it && ((it as { type?: string }).type === 'divider' || !PROMOTED_INLINE_KEYS.has((it as { key?: string }).key ?? '')),
  );
  const out: NonNullable<MenuProps['items']> = [];
  for (const it of kept) {
    const isDivider = (it as { type?: string }).type === 'divider';
    const prevIsDivider = out.length > 0 && (out[out.length - 1] as { type?: string }).type === 'divider';
    if (isDivider && (out.length === 0 || prevIsDivider)) continue;
    out.push(it);
  }
  while (out.length && (out[out.length - 1] as { type?: string }).type === 'divider') out.pop();
  return out;
}

type ScheduleDialogState = {
  open: boolean;
  source: 'bulk' | 'row';
  items: CalendarItem[];
  selectedDate: Dayjs | null;
  numDays: number;
  timeOfDay: string;
};

type BatchCountResult = {
  succeeded: number;
  failed: number;
  succeededIds?: string[];
};

type BatchScheduleResponse = {
  scheduled: number;
  failed: number;
  results: Array<{
    content_id: string;
    scheduled_time: string | null;
    success: boolean;
    error?: string | null;
  }>;
};

type BatchDeleteResponse = {
  deleted: number;
  failed: number;
};

type BatchRetryResponse = {
  restarted: number;
  failed: number;
  results: Array<{
    content_id: string;
    success: boolean;
    status?: string | null;
    mode?: string | null;
    job_id?: string | null;
    error?: string | null;
  }>;
};

const initialScheduleDialog: ScheduleDialogState = {
  open: false,
  source: 'row',
  items: [],
  selectedDate: null,
  numDays: 5,
  timeOfDay: '10:00',
};

/**
 * Dims the current page of rows and floats a spinner while the next page is
 * fetched. Rows stay mounted (react-query keeps the previous page via
 * placeholderData) so the list never flashes empty — it just goes quiet.
 */
const PageTransitionOverlay: React.FC = () => (
  <div
    role="status"
    aria-live="polite"
    aria-label="Loading page"
    style={{
      position: 'absolute',
      inset: 0,
      zIndex: 5,
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      paddingTop: 48,
      background:
        'color-mix(in srgb, var(--color-warm-bg) 55%, transparent)',
    }}
  >
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: '7px 14px',
        borderRadius: 999,
        border: '1px solid var(--color-warm-border)',
        background: 'var(--color-paper)',
        color: 'var(--color-ink-2)',
        fontSize: 12,
        boxShadow: '0 2px 10px rgba(0,0,0,0.07)',
      }}
    >
      <RefreshCw size={13} className="animate-spin" />
      Loading page…
    </span>
  </div>
);

const ContentPlannerPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const viewParam = searchParams.get('view');
  const pageParam = searchParams.get('page');
  const highlightContentId = searchParams.get('new') ?? undefined;

  const [view, setView] = useState<ViewMode>(() =>
    isViewMode(viewParam) ? viewParam : 'table',
  );
  const [search, setSearch] = useState('');
  const deferredSearch = useDeferredValue(search.trim());
  const [statusFilter, setStatusFilter] = useState('all');
  // Page is seeded from the URL so it survives navigating into the editor and
  // coming back: the editor is a sibling route, so this component unmounts and
  // plain local state would be lost (resetting the user to page 1).
  const [page, setPage] = useState(() => {
    const parsed = Number(pageParam);
    return Number.isFinite(parsed) && parsed >= 1 ? Math.floor(parsed) : 1;
  });
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [busyIds, setBusyIds] = useState<Set<string>>(new Set());
  const [scheduleDialog, setScheduleDialog] =
    useState<ScheduleDialogState>(initialScheduleDialog);

  const [wizardOpen, setWizardOpen] = useState(() =>
    searchParams.get('open') === 'true',
  );
  // New Article dropdown (#1340): which mode to open the wizard straight into.
  const [wizardMode, setWizardMode] = useState<'quick-create' | 'bulk' | null>(null);

  // The global Internal/Client lens decides the New Article UI.
  const planUserProfile = useWorkspaceStore((s) => s.userProfile);
  const planAdsViewMode = useAdsViewStore((s) => s.viewMode);
  const bulkComingSoon =
    !canAccessInternalAdsTools(planUserProfile) || planAdsViewMode === 'client';
  const openWizardMode = useCallback((mode: 'quick-create' | 'bulk') => {
    setWizardMode(mode);
    setWizardOpen(true);
  }, []);

  const { processingCount } = useAutoPublishManager();

  useEffect(() => {
    if (searchParams.get('open') === 'true') {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('open');
          return next;
        },
        { replace: true },
      );
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSetView = useCallback(
    (newView: ViewMode) => {
      setView(newView);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('view', newView);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // `setSearchParams` gets a NEW identity whenever the location changes, so it
  // must NOT be a dependency of `handleSetPage`: turning the page rewrites the
  // URL -> new `setSearchParams` -> new `handleSetPage` -> every effect that
  // depends on it re-fires. That loop is exactly what bounced the list back to
  // page 1 on each page turn. Hold it in a ref so `handleSetPage` is stable.
  const setSearchParamsRef = useRef(setSearchParams);
  useEffect(() => {
    setSearchParamsRef.current = setSearchParams;
  });

  // Mirror `page` into the URL (replace, so paging doesn't spam history).
  // Page 1 drops the param to keep the default URL clean.
  const handleSetPage = useCallback((nextPage: number) => {
    const clamped = Math.max(1, Math.floor(nextPage));
    setPage(clamped);
    setSearchParamsRef.current(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (clamped <= 1) next.delete('page');
        else next.set('page', String(clamped));
        return next;
      },
      { replace: true },
    );
  }, []);

  const { currentTenant } = useTenantStore();

  // The Content Planner is scoped to the SINGLE selected workspace. It previously
  // aggregated every active workspace the user belonged to, which leaked other
  // tenants' content into the feed (e.g. "AAA Fence" showing "Precision Car
  // Restoration" keywords). Always scope to the currently-selected tenant.
  const tenantIds = useMemo(
    () => (currentTenant ? [currentTenant] : []),
    [currentTenant],
  );

  const fetchRows = view === 'list' || view === 'table';

  // What an agent can drive on the table, registered rather than hand-listed,
  // so the intro label names what is REALLY wired and cannot drift when a tool
  // is renamed. Registered only while the table is the visible surface.
  const registerTouchables = useAgentActivity((s) => s.registerTouchables);
  const clearTouchables = useAgentActivity((s) => s.clearTouchables);
  useEffect(() => {
    if (view !== 'table') return;
    const keys = ['planner-table-rows', 'planner-table-create', 'planner-table-publish', 'planner-table-restart'];
    registerTouchables([
      { key: keys[0], label: 'article rows', can: 'list and filter them by status, title or month' },
      { key: keys[1], label: 'new article', can: 'create one from a keyword' },
      { key: keys[2], label: 'publish', can: 'publish a finished article, refusing on gate violations' },
      { key: keys[3], label: 'restart generation', can: 're-run one with an explicit writer' },
    ]);
    return () => clearTouchables(keys);
  }, [view, registerTouchables, clearTouchables]);
  const {
    items,
    stats,
    total,
    isLoading,
    isPlaceholderData,
  } = useMultiWorkspaceCalendar(tenantIds, {
    limit: fetchRows ? PAGE_SIZE : 1,
    offset: fetchRows ? (page - 1) * PAGE_SIZE : 0,
    search: fetchRows ? deferredSearch || undefined : undefined,
    status: fetchRows && statusFilter !== 'all' ? statusFilter : undefined,
    enabled: tenantIds.length > 0,
  });

  // True while a page/filter change is in flight and the PREVIOUS page is still
  // on screen. `isLoading` stays false here (placeholderData supplies data), so
  // this is the signal the page-transition spinner and disabled controls use.
  const isPageTransition = fetchRows && isPlaceholderData;
  // Any state where the list must not accept another page turn.
  const isListBusy = isLoading || isPageTransition;

  const visibleItems = fetchRows ? items : [];
  const totalCount = stats.total || total;
  const totalPages = Math.max(1, Math.ceil((total || 0) / PAGE_SIZE));
  const pageStart = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const pageEnd = Math.min(page * PAGE_SIZE, total || 0);

  const selectedItems = useMemo(
    () => visibleItems.filter((item) => selectedIds.includes(item.id)),
    [visibleItems, selectedIds],
  );
  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const visibleItemsById = useMemo(
    () => new Map(visibleItems.map((item) => [item.id, item])),
    [visibleItems],
  );
  const scheduleDialogOpen =
    scheduleDialog.open &&
    scheduleDialog.items.some((item) => {
      const latestItem = visibleItemsById.get(item.id) ?? item;
      return !isScheduled(latestItem);
    }) &&
    (scheduleDialog.source !== 'bulk' ||
      scheduleDialog.items.some((item) => selectedIdSet.has(item.id)));
  const {
    publishableItems,
    draftableItems,
    schedulableItems,
    failedItems,
  } = useMemo(
    () => getBulkActionBuckets(selectedItems),
    [selectedItems],
  );

  // Reset to page 1 ONLY when the filters genuinely change value — never on
  // mount. A boolean "skip first run" guard is NOT enough: React StrictMode
  // (main.tsx) mounts twice in dev, re-running this effect with the ref already
  // set, which stomped a page restored from the URL back to 1. Comparing the
  // actual filter values makes the effect idempotent under any number of
  // duplicate invocations.
  const filtersKey = `${deferredSearch} ${statusFilter} ${tenantIds.join('|')}`;
  const prevFiltersKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (prevFiltersKeyRef.current === filtersKey) return; // same values — no-op
    const isFirst = prevFiltersKeyRef.current === null;
    prevFiltersKeyRef.current = filtersKey;
    if (isFirst) return; // mount: keep the page restored from the URL
    handleSetPage(1);
    setSelectedIds([]);
    // handleSetPage is identity-stable (empty deps) and deliberately omitted:
    // including it re-fires this effect on every URL change and resets page 1.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey]);

  useEffect(() => {
    setSelectedIds([]);
  }, [view, page]);

  useEffect(() => {
    // Only clamp once a real total is in hand. While a page fetch is in flight
    // `total` can be unknown, which would collapse totalPages to 1 and bounce
    // the user back to page 1 mid-navigation.
    if (isLoading) return;
    if (page > totalPages) handleSetPage(totalPages);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, totalPages, isLoading]);

  const refreshPlanner = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: [...contentPlannerKeys.all, 'calendar'],
    });
    queryClient.invalidateQueries({ queryKey: ['blog-creation-content'] });
  }, [queryClient]);

  const tenantForItem = useCallback(
    (item: CalendarItem) =>
      tenantForCalendarItem(item, currentTenant || tenantIds[0] || ''),
    [currentTenant, tenantIds],
  );

  const markBusy = useCallback((ids: string[], busy: boolean) => {
    setBusyIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => {
        if (busy) next.add(id);
        else next.delete(id);
      });
      return next;
    });
  }, []);

  const getContentDetail = useCallback(
    async (item: CalendarItem): Promise<ContentDetail> => {
      const tenantId = tenantForItem(item);
      if (!tenantId) throw new Error(`Missing tenant for ${item.id}`);
      const response = await apiClient.get<ContentDetail>(
        `/blog-creation/content/${item.id}?tenant_id=${encodeTenant(tenantId)}`,
      );
      return response.data;
    },
    [tenantForItem],
  );

  const contentHtmlFromDetail = useCallback((detail: ContentDetail, siteHost?: string | null): string => {
    if (detail.content_json) {
      return generateHTMLFromRTE(detail.content_json, siteHost);
    }
    return detail.content_html || '';
  }, []);

  const confirmLinkWarnings = useCallback(
    (detail: ContentDetail, item: CalendarItem): Promise<boolean> => {
      if (!detail.has_link_warnings || !detail.link_warnings) {
        return Promise.resolve(true);
      }

      const warnings = detail.link_warnings;
      return new Promise((resolve) => {
        Modal.confirm({
          title: 'Publish with link warnings?',
          content: (
            <div className="text-sm text-muted-foreground">
              <div className="mb-2 font-medium text-foreground">{titleFor(item)}</div>
              <div>
                Internal links: {warnings.internal}/{warnings.required_internal}
              </div>
              <div>
                External links: {warnings.external}/{warnings.required_external}
              </div>
            </div>
          ),
          okText: 'Publish anyway',
          cancelText: 'Cancel',
          onOk: () => resolve(true),
          onCancel: () => resolve(false),
        });
      });
    },
    [],
  );

  const runAction = useCallback(
    async (
      actionItems: CalendarItem[],
      action: (item: CalendarItem) => Promise<boolean | void>,
      successMessage: (count: number) => string,
      failureMessage: string,
    ) => {
      if (actionItems.length === 0) return;
      const ids = actionItems.map((item) => item.id);
      markBusy(ids, true);
      try {
        const results = await Promise.allSettled(
          actionItems.map(async (item) => {
            try {
              const result = await action(item);
              // A `false` return is a user cancellation (e.g. link-warning
              // "Cancel"), NOT a failure — treat it as a neutral skip.
              const outcome: 'succeeded' | 'cancelled' = result === false ? 'cancelled' : 'succeeded';
              return { id: item.id, outcome };
            } catch (err) {
              // "Go back" in the pre-publish metadata gate is a cancellation,
              // not a failure — never surface it as an error toast.
              if (isPublishCancelledError(err)) {
                return { id: item.id, outcome: 'cancelled' as const };
              }
              throw err;
            }
          }),
        );
        const succeededIds = results
          .filter(
            (result): result is PromiseFulfilledResult<{ id: string; outcome: 'succeeded' | 'cancelled' }> =>
              result.status === 'fulfilled' && result.value.outcome === 'succeeded',
          )
          .map((result) => result.value.id);
        const succeeded = succeededIds.length;
        // Only genuine rejections count as failures; cancellations are silent.
        const failed = results.filter((result) => result.status === 'rejected').length;

        if (succeeded > 0) toast.success(successMessage(succeeded));
        if (failed > 0) toast.error(`${failureMessage}: ${failed} failed`);

        if (succeededIds.length > 0) {
          setSelectedIds((prev) => prev.filter((id) => !succeededIds.includes(id)));
        }
        refreshPlanner();
      } finally {
        markBusy(ids, false);
      }
    },
    [markBusy, refreshPlanner],
  );

  const runTenantBatchAction = useCallback(
    async (
      actionItems: CalendarItem[],
      action: (tenantId: string, itemsForTenant: CalendarItem[]) => Promise<BatchCountResult>,
      successMessage: (count: number) => string,
      failureMessage: string,
    ) => {
      if (actionItems.length === 0) return;
      const ids = actionItems.map((item) => item.id);
      markBusy(ids, true);
      try {
        const groups = groupCalendarItemsByTenant(
          actionItems,
          currentTenant || tenantIds[0] || '',
        );
        const groupedCount = Array.from(groups.values()).reduce(
          (sum, group) => sum + group.length,
          0,
        );
        const missingTenantCount = actionItems.length - groupedCount;

        const responses = await Promise.all(
          Array.from(groups.entries()).map(async ([tenantId, itemsForTenant]) => {
            try {
              return await action(tenantId, itemsForTenant);
            } catch {
              return { succeeded: 0, failed: itemsForTenant.length };
            }
          }),
        );

        const succeeded = responses.reduce((sum, result) => sum + result.succeeded, 0);
        const succeededIds = responses.flatMap((result) => result.succeededIds ?? []);
        const failed =
          missingTenantCount +
          responses.reduce((sum, result) => sum + result.failed, 0);

        if (succeeded > 0) toast.success(successMessage(succeeded));
        if (failed > 0) toast.error(`${failureMessage}: ${failed} failed`);

        if (succeededIds.length > 0) {
          setSelectedIds((prev) => prev.filter((id) => !succeededIds.includes(id)));
        }
        refreshPlanner();
      } finally {
        markBusy(ids, false);
      }
    },
    [currentTenant, markBusy, refreshPlanner, tenantIds],
  );

  const publishItem = useCallback(
    async (item: CalendarItem, publishImmediately = true): Promise<boolean> => {
      const tenantId = tenantForItem(item);
      if (!tenantId) throw new Error(`Missing tenant for ${item.id}`);
      const detail = await getContentDetail(item);
      if (publishImmediately) {
        const proceed = await confirmLinkWarnings(detail, item);
        if (!proceed) return false;
      }

      const htmlContent = contentHtmlFromDetail(detail, tenantId);
      if (!htmlContent) throw new Error('No content available to publish');

      // Route through the shared service so the universal pre-publish metadata
      // gate runs (raw apiClient.post bypassed it). Preserves the original
      // request semantics: html_content, publish_immediately, and the optional
      // builder_native_override derived from item.use_bridge.
      const builderNativeOverride =
        item.use_bridge !== undefined && item.use_bridge !== null ? item.use_bridge : undefined;
      await blogCreationService.publish(
        item.id,
        htmlContent,
        publishImmediately,
        builderNativeOverride,
        tenantId,
      );
      return true;
    },
    [confirmLinkWarnings, contentHtmlFromDetail, getContentDetail, tenantForItem],
  );

  const draftItem = useCallback(
    async (item: CalendarItem) => {
      const tenantId = tenantForItem(item);
      if (!tenantId) throw new Error(`Missing tenant for ${item.id}`);
      await apiClient.patch(
        `/blog-creation/content/${item.id}/wordpress-status?tenant_id=${encodeTenant(tenantId)}`,
        { wordpress_status: 'draft' },
      );
      if (item.wp_url || item.wordpress_post_id) {
        await publishItem(item, false);
      }
    },
    [publishItem, tenantForItem],
  );

  const syncItem = useCallback(
    async (item: CalendarItem) => {
      await publishItem(item, item.wordpress_status === 'publish');
    },
    [publishItem],
  );

  const retryItem = useCallback(
    async (item: CalendarItem) => {
      const tenantId = tenantForItem(item);
      if (!tenantId) throw new Error(`Missing tenant for ${item.id}`);
      await apiClient.post(
        `/blog-creation/content/${item.id}/regenerate?tenant_id=${encodeTenant(tenantId)}`,
        {
          stage: 'all',
          model_override: REGENERATE_WRITER_MODEL,
          wordpress_publish_mode: 'none',
        },
      );
    },
    [tenantForItem],
  );

  // Per-item Generate for an EXISTING titled row/chip. Reuses the regenerate
  // enqueue so a driver never has to open the creation wizard to start a run.
  const handleGenerateItem = useCallback(
    (item: CalendarItem) => {
      void runAction(
        [item],
        retryItem,
        () => `Queued ${titleFor(item)}`,
        'Generation failed',
      );
    },
    [retryItem, runAction],
  );

  const deleteItems = useCallback(
    (deleteTargets: CalendarItem[]) => {
      if (deleteTargets.length === 0) return;
      Modal.confirm({
        title: `Delete ${deleteTargets.length} ${deleteTargets.length === 1 ? 'article' : 'articles'}?`,
        content: 'This moves the selected posts to deleted items and removes them from the planner.',
        okText: 'Delete',
        okType: 'danger',
        cancelText: 'Cancel',
        onOk: () =>
          runTenantBatchAction(
            deleteTargets,
            async (tenantId, itemsForTenant) => {
              const response = await apiClient.post<BatchDeleteResponse>(
                `/blog-creation/content/batch-delete?tenant_id=${encodeTenant(tenantId)}`,
                { content_ids: itemsForTenant.map((item) => item.id) },
              );
              return {
                succeeded: response.data.deleted,
                failed: response.data.failed,
                succeededIds:
                  response.data.failed === 0
                    ? itemsForTenant.map((item) => item.id)
                    : [],
              };
            },
            (count) => `Deleted ${count} ${count === 1 ? 'article' : 'articles'}`,
            'Delete failed',
          ),
      });
    },
    [runTenantBatchAction],
  );

  const requestIndexing = useCallback(
    async (item: CalendarItem) => {
      const tenantId = tenantForItem(item);
      if (!tenantId) throw new Error(`Missing tenant for ${item.id}`);
      const response = await apiClient.post<{
        google?: { success?: boolean };
        bing?: { success?: boolean };
      }>(
        `/blog-creation/content/${item.id}/request-indexing?tenant_id=${encodeTenant(tenantId)}`,
      );
      const engines = [];
      if (response.data.google?.success) engines.push('Google');
      if (response.data.bing?.success) engines.push('Bing');
      if (engines.length === 0) {
        toast.warning('Indexing services are not configured for this tenant');
        return false;
      }
      return true;
    },
    [tenantForItem],
  );

  const cancelSchedule = useCallback(
    async (item: CalendarItem) => {
      const tenantId = tenantForItem(item);
      if (!tenantId) throw new Error(`Missing tenant for ${item.id}`);
      await apiClient.delete(
        `/blog-creation/content/${item.id}/schedule?tenant_id=${encodeTenant(tenantId)}`,
      );
    },
    [tenantForItem],
  );

  const openScheduleDialog = useCallback(
    (itemsToSchedule: CalendarItem[], source: 'bulk' | 'row' = 'row') => {
      if (itemsToSchedule.length === 0) return;
      setScheduleDialog({
        open: true,
        source,
        items: itemsToSchedule,
        selectedDate: dayjs().add(1, 'day').hour(10).minute(0).second(0),
        numDays: Math.min(itemsToSchedule.length, 5),
        timeOfDay: '10:00',
      });
    },
    [],
  );

  const closeScheduleDialog = useCallback(() => {
    flushSync(() => {
      setScheduleDialog({ ...initialScheduleDialog });
    });
  }, []);

  const confirmSchedule = useCallback(
    async (dialog = scheduleDialog) => {
      if (!dialog.selectedDate || dialog.items.length === 0) return;
      const targets = dialog.items;
      const startDate = dialog.selectedDate.startOf('day');
      const [hour, minute] = dialog.timeOfDay.split(':').map(Number);
      const days = Math.min(30, Math.max(1, dialog.numDays || 1));

      try {
        // Pre-publish metadata gate: scheduling sets a publish time, so warn
        // about metadata issues now (one combined dialog for all targets).
        // Best-effort — a fetch/gate error must never block scheduling.
        try {
          const gateItems = await Promise.all(
            targets.map(async (item) =>
              buildGateItem(await getContentDetail(item), tenantForItem(item)),
            ),
          );
          if (!(await confirmPublish(gateItems))) return;
        } catch (err) {
          if (isPublishCancelledError(err)) return;
          // swallow other errors: never block scheduling on the gate
        }

        if (targets.length > 1) {
          await runTenantBatchAction(
            targets,
            async (tenantId, itemsForTenant) => {
              const response = await apiClient.post<BatchScheduleResponse>(
                `/blog-creation/content/batch-schedule?tenant_id=${encodeTenant(tenantId)}`,
                {
                  content_ids: itemsForTenant.map((item) => item.id),
                  num_days: days,
                  start_date: startDate.toISOString(),
                  time_of_day: dialog.timeOfDay,
                },
              );
              return {
                succeeded: response.data.scheduled,
                failed: response.data.failed,
                succeededIds: response.data.results
                  .filter((result) => result.success)
                  .map((result) => result.content_id),
              };
            },
            (count) => `Scheduled ${count} ${count === 1 ? 'article' : 'articles'}`,
            'Schedule failed',
          );
          return;
        }

        await runAction(
          targets,
          async (item) => {
            const tenantId = tenantForItem(item);
            if (!tenantId) throw new Error(`Missing tenant for ${item.id}`);
            const position = targets.findIndex((candidate) => candidate.id === item.id);
            const dayOffset =
              targets.length <= 1
                ? 0
                : Math.floor((Math.max(0, position) * days) / targets.length);
            const scheduledTime = startDate
              .add(dayOffset, 'day')
              .hour(hour || 10)
              .minute(minute || 0)
              .second(0)
              .millisecond(0)
              .toISOString();
            await apiClient.patch(
              `/blog-creation/content/${item.id}/schedule?tenant_id=${encodeTenant(tenantId)}`,
              { scheduled_time: scheduledTime },
            );
          },
          (count) => `Scheduled ${count} ${count === 1 ? 'article' : 'articles'}`,
          'Schedule failed',
        );
      } finally {
        closeScheduleDialog();
      }
    },
    [
      closeScheduleDialog,
      getContentDetail,
      runAction,
      runTenantBatchAction,
      scheduleDialog,
      tenantForItem,
    ],
  );

  const handleToggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }, []);

  // Navigate into the editor carrying the CURRENT list URL (incl. ?page=N) in
  // router state, so the editor's breadcrumb/back can return the user to the
  // exact page they left — not a bare /content/planner that resets to page 1.
  const openEditor = useCallback(
    (contentId: string) => {
      navigate(editorPath(contentId), {
        state: { from: `${location.pathname}${location.search}` },
      });
    },
    [navigate, location.pathname, location.search],
  );

  const handleRowClick = useCallback(
    (item: CalendarItem) => {
      openEditor(item.id);
    },
    [openEditor],
  );

  const getRowMenuItems = useCallback(
    (item: CalendarItem): MenuProps['items'] => {
      const published = isPublished(item);
      const scheduled = isScheduled(item);
      const completed = isCompleted(item);
      const processing = isProcessing(item);
      const busy = busyIds.has(item.id);
      const itemTitle = titleFor(item);

      return pruneMenu([
        {
          key: 'details',
          label: 'View Details',
          icon: <FileText size={14} />,
          onClick: () => openEditor(item.id),
        },
        ...(completed
          ? [
              {
                key: 'preview',
                label: 'Preview Content',
                icon: <Eye size={14} />,
                onClick: () => openEditor(item.id),
              },
              {
                key: 'editor',
                label: 'Open in Full Editor',
                icon: <ExternalLink size={14} />,
                onClick: () => openEditor(item.id),
              },
              {
                key: 'qa',
                label: 'Open QA Review',
                icon: <Search size={14} />,
                onClick: () => navigate('/content/qa'),
              },
            ]
          : []),
        ...(item.status.includes('_review') || item.generation_mode === 'supervised'
          ? [
              {
                key: 'supervised-review',
                label: 'Continue Supervised Review',
                icon: <Eye size={14} />,
                onClick: () => navigate(`/content/supervised-review/${item.id}`),
              },
            ]
          : []),
        ...(item.gdocs_doc_url
          ? [
              {
                key: 'gdocs',
                label: 'Open Google Doc',
                icon: <FileText size={14} />,
                onClick: () => window.open(item.gdocs_doc_url!, '_blank', 'noopener,noreferrer'),
              },
            ]
          : []),
        ...(completed && !published
          ? [
              { type: 'divider' as const },
              {
                key: 'publish',
                label: busy ? 'Publishing...' : 'Publish to WordPress',
                icon: <UploadCloud size={14} />,
                disabled: busy,
                onClick: () =>
                  runAction(
                    [item],
                    (target) => publishItem(target, true),
                    () => `Published ${itemTitle}`,
                    'Publish failed',
                  ),
              },
              {
                key: 'sync',
                label: busy ? 'Syncing...' : 'Sync to WordPress',
                icon: <UploadCloud size={14} />,
                disabled: busy,
                onClick: () =>
                  runAction(
                    [item],
                    syncItem,
                    () => `Synced ${itemTitle}`,
                    'Sync failed',
                  ),
              },
            ]
          : []),
        ...(published
          ? [
              { type: 'divider' as const },
              {
                key: 'draft',
                label: busy ? 'Drafting...' : 'Set to Draft',
                icon: <FileText size={14} />,
                disabled: busy,
                onClick: () =>
                  runAction(
                    [item],
                    draftItem,
                    () => `Drafted ${itemTitle}`,
                    'Draft failed',
                  ),
              },
              {
                key: 'request-indexing',
                label: 'Request Indexing',
                icon: <Search size={14} />,
                onClick: () =>
                  runAction(
                    [item],
                    requestIndexing,
                    () => 'Indexing requested',
                    'Indexing request failed',
                  ),
              },
            ]
          : []),
        ...(completed && !published && !scheduled
          ? [
              {
                key: 'schedule',
                label: 'Schedule Publish',
                icon: <CalendarDays size={14} />,
                onClick: () => openScheduleDialog([item]),
              },
            ]
          : []),
        ...(scheduled
          ? [
              {
                key: 'cancel-schedule',
                label: 'Cancel Schedule',
                icon: <CalendarDays size={14} />,
                onClick: () =>
                  runAction(
                    [item],
                    cancelSchedule,
                    () => `Schedule cancelled for ${itemTitle}`,
                    'Cancel schedule failed',
                  ),
              },
            ]
          : []),
        ...(item.status === 'failed'
          ? [
              { type: 'divider' as const },
              {
                key: 'retry',
                label: busy ? 'Restarting...' : 'Restart Generation',
                icon: <RotateCcw size={14} />,
                disabled: busy,
                onClick: () =>
                  runAction(
                    [item],
                    retryItem,
                    () => `Restarted ${itemTitle}`,
                    'Restart failed',
                  ),
              },
            ]
          : []),
        ...(isPublished(item) && item.wp_url
          ? [
              {
                key: 'live',
                label: 'Open Live Post',
                icon: <Link2 size={14} />,
                onClick: () => window.open(item.wp_url!, '_blank', 'noopener,noreferrer'),
              },
            ]
          : []),
        { type: 'divider' as const },
        {
          key: 'delete',
          label: processing ? 'Delete from Queue' : 'Delete',
          icon: <Trash2 size={14} />,
          danger: true,
          disabled: busy,
          onClick: () => deleteItems([item]),
        },
      ]);
    },
    [
      busyIds,
      cancelSchedule,
      deleteItems,
      draftItem,
      navigate,
      openScheduleDialog,
      publishItem,
      requestIndexing,
      retryItem,
      runAction,
      syncItem,
    ],
  );

  // Primary, status-gated actions surfaced as prominent inline buttons in the
  // table + list rows (the rest stay in the ⋯ menu via getRowMenuItems).
  const getRowPrimaryActions = useCallback(
    (item: CalendarItem): RowPrimaryAction[] => {
      const published = isPublished(item);
      const completed = isCompleted(item);
      const processing = isProcessing(item);
      const busy = busyIds.has(item.id);
      const itemTitle = titleFor(item);
      const actions: RowPrimaryAction[] = [];

      if (published) {
        actions.push({
          key: 'draft',
          label: busy ? 'Drafting…' : 'Set to Draft',
          icon: <FileText size={14} />,
          disabled: busy,
          onClick: () =>
            runAction([item], draftItem, () => `Drafted ${itemTitle}`, 'Draft failed'),
        });
        if (item.wp_url) {
          actions.push({
            key: 'live',
            label: 'Open Live',
            icon: <Link2 size={14} />,
            onClick: () => window.open(item.wp_url!, '_blank', 'noopener,noreferrer'),
          });
        }
      } else if (item.status === 'failed') {
        actions.push({
          key: 'retry',
          label: busy ? 'Restarting…' : 'Restart',
          icon: <RotateCcw size={14} />,
          disabled: busy,
          tone: 'primary',
          onClick: () =>
            runAction([item], retryItem, () => `Restarted ${itemTitle}`, 'Restart failed'),
        });
      } else {
        if (completed) {
          actions.push({
            key: 'publish',
            label: busy ? 'Publishing…' : 'Publish',
            icon: <UploadCloud size={14} />,
            disabled: busy,
            tone: 'primary',
            onClick: () =>
              runAction(
                [item],
                (target) => publishItem(target, true),
                () => `Published ${itemTitle}`,
                'Publish failed',
              ),
          });
          actions.push({
            key: 'sync',
            label: busy ? 'Syncing…' : 'Sync',
            icon: <RefreshCw size={14} />,
            disabled: busy,
            onClick: () =>
              runAction([item], syncItem, () => `Synced ${itemTitle}`, 'Sync failed'),
          });
        }
        actions.push({
          key: 'open',
          label: 'Open',
          icon: <ExternalLink size={14} />,
          onClick: () => openEditor(item.id),
        });
      }

      actions.push({
        key: 'delete',
        label: processing ? 'Delete from queue' : 'Delete',
        icon: <Trash2 size={14} />,
        danger: true,
        disabled: busy,
        onClick: () => deleteItems([item]),
      });

      return actions;
    },
    [
      busyIds,
      deleteItems,
      draftItem,
      navigate,
      publishItem,
      retryItem,
      runAction,
      syncItem,
    ],
  );

  const handleBulkPublish = useCallback(async () => {
    if (publishableItems.length === 0) return;
    const ids = publishableItems.map((item) => item.id);
    markBusy(ids, true);
    try {
      const result = await publishContentPlannerItems(publishableItems, {
        fallbackTenantId: currentTenant || tenantIds[0] || null,
        confirmLinkWarnings: (item, detail) => confirmLinkWarnings(detail, item),
      });
      if (result.succeeded > 0) {
        toast.success(
          `Published ${result.succeeded} ${result.succeeded === 1 ? 'article' : 'articles'}`,
        );
      }
      if (result.skipped > 0) {
        toast.info(`${result.skipped} ${result.skipped === 1 ? 'article was' : 'articles were'} skipped`);
      }
      if (result.failed > 0) {
        toast.error(`Publish failed: ${result.failed} failed`);
      }
      const completedIds = result.results
        .filter((item) => item.status === 'success')
        .map((item) => item.content_id);
      if (completedIds.length > 0) {
        setSelectedIds((prev) => prev.filter((id) => !completedIds.includes(id)));
      }
      refreshPlanner();
    } finally {
      markBusy(ids, false);
    }
  }, [confirmLinkWarnings, currentTenant, markBusy, publishableItems, refreshPlanner, tenantIds]);

  const handleBulkDraft = useCallback(async () => {
    if (draftableItems.length === 0) return;
    const ids = draftableItems.map((item) => item.id);
    markBusy(ids, true);
    try {
      const result = await draftContentPlannerItems(draftableItems, {
        fallbackTenantId: currentTenant || tenantIds[0] || null,
      });
      if (result.succeeded > 0) {
        toast.success(
          `Drafted ${result.succeeded} ${result.succeeded === 1 ? 'article' : 'articles'}`,
        );
      }
      if (result.failed > 0) {
        toast.error(`Draft failed: ${result.failed} failed`);
      }
      const completedIds = result.results
        .filter((item) => item.status === 'success')
        .map((item) => item.content_id);
      if (completedIds.length > 0) {
        setSelectedIds((prev) => prev.filter((id) => !completedIds.includes(id)));
      }
      refreshPlanner();
    } finally {
      markBusy(ids, false);
    }
  }, [currentTenant, draftableItems, markBusy, refreshPlanner, tenantIds]);

  const handleBulkRetry = useCallback(() => {
    runTenantBatchAction(
      failedItems,
      async (tenantId, itemsForTenant) => {
        const response = await apiClient.post<BatchRetryResponse>(
          `/blog-creation/content/batch-regenerate?tenant_id=${encodeTenant(tenantId)}`,
          {
            content_ids: itemsForTenant.map((item) => item.id),
            stage: 'all',
          },
        );
        return {
          succeeded: response.data.restarted,
          failed: response.data.failed,
          succeededIds: response.data.results
            .filter((result) => result.success)
            .map((result) => result.content_id),
        };
      },
      (count) => `Restarted ${count} ${count === 1 ? 'article' : 'articles'}`,
      'Restart failed',
    );
  }, [failedItems, runTenantBatchAction]);

  const renderPagination = () => {
    if (!fetchRows || total <= PAGE_SIZE) return null;
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          padding: '12px 28px',
          borderTop: '1px solid var(--color-warm-border)',
          background: 'var(--color-warm-bg)',
          color: 'var(--color-ink-2)',
          fontSize: 12,
        }}
      >
        <span>
          Showing {pageStart}-{pageEnd} of {total} articles
        </span>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <button
            type="button"
            onClick={() => handleSetPage(page - 1)}
            disabled={page === 1 || isListBusy}
            aria-label="Previous page"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              padding: '6px 10px',
              borderRadius: 7,
              border: '1px solid var(--color-warm-border)',
              background: 'var(--color-paper)',
              color: 'var(--color-ink-2)',
              cursor: page === 1 || isListBusy ? 'not-allowed' : 'pointer',
              opacity: page === 1 || isListBusy ? 0.55 : 1,
            }}
          >
            <ChevronLeft size={14} />
            Prev
          </button>
          <span
            className="mono"
            style={{
              color: 'var(--color-ink-3)',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            {isPageTransition && (
              <RefreshCw size={12} className="animate-spin" aria-hidden="true" />
            )}
            Page {page} / {totalPages}
          </span>
          <button
            type="button"
            onClick={() => handleSetPage(Math.min(totalPages, page + 1))}
            disabled={page >= totalPages || isListBusy}
            aria-label="Next page"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              padding: '6px 10px',
              borderRadius: 7,
              border: '1px solid var(--color-warm-border)',
              background: 'var(--color-paper)',
              color: 'var(--color-ink-2)',
              cursor: page >= totalPages || isListBusy ? 'not-allowed' : 'pointer',
              opacity: page >= totalPages || isListBusy ? 0.55 : 1,
            }}
          >
            Next
            <ChevronRight size={14} />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="h-full overflow-auto relative">
      <PageHeader
        totalCount={totalCount}
        onNewContent={() => setWizardOpen(true)}
        showModeDropdown={true}
        onQuickCreate={() => openWizardMode('quick-create')}
        onBulkGenerate={() => openWizardMode('bulk')}
        bulkDisabled={bulkComingSoon}
      />

      {view !== 'calendar' && (
        <SummaryStrip
          stats={stats}
          activeFilter={statusFilter}
          onCardClick={fetchRows ? setStatusFilter : undefined}
        />
      )}

      <FilterBar
        view={view}
        onViewChange={handleSetView}
        search={search}
        onSearchChange={setSearch}
        status={statusFilter}
        onStatusChange={setStatusFilter}
        onRefresh={refreshPlanner}
        showSearch={view !== 'calendar'}
        showStatusFilter={fetchRows}
        showGroup={false}
      />

      {view === 'list' && (
        <>
          <div style={{ position: 'relative' }}>
          <div
            style={{
              opacity: isPageTransition ? 0.45 : 1,
              pointerEvents: isPageTransition ? 'none' : undefined,
              transition: 'opacity 150ms ease',
            }}
          >
          <ContentListView
            items={visibleItems}
            isLoading={isLoading}
            selectedIds={selectedIds}
            onToggleSelect={handleToggleSelect}
            onRowClick={handleRowClick}
            getRowMenuItems={getRowMenuItems}
            getRowPrimaryActions={getRowPrimaryActions}
          />
          </div>
          {isPageTransition && <PageTransitionOverlay />}
          </div>
          {renderPagination()}
        </>
      )}

      {view === 'table' && (
        <>
          {/* WebMCP affordances. The intro names what an agent can drive and
              self-dismisses; the bot appears only while a tool is actually
              running; the ring lights the component being driven. */}
          <AgentSurfaceIntro surfaceKey="planner-table" />
          <AgentActivityDock />
          <div style={{ position: 'relative' }}>
          <div
            style={{
              opacity: isPageTransition ? 0.45 : 1,
              pointerEvents: isPageTransition ? 'none' : undefined,
              transition: 'opacity 150ms ease',
            }}
          >
          <div data-agent-target="planner-table-rows">
          <ContentTrackerTable
            items={visibleItems}
            isLoading={isLoading}
            selectedIds={selectedIds}
            onToggleSelect={handleToggleSelect}
            onViewDetails={handleRowClick}
            onGenerate={handleGenerateItem}
            onSchedule={(item) => openScheduleDialog([item])}
            onCreateNew={() => setWizardOpen(true)}
            getRowMenuItems={getRowMenuItems}
            getRowPrimaryActions={getRowPrimaryActions}
            highlightContentId={highlightContentId}
          />
          </div>
          </div>
          {isPageTransition && <PageTransitionOverlay />}
          </div>
          {renderPagination()}
        </>
      )}

      {view === 'calendar' && (
        <ContentCalendarView
          onCreateForDate={() => setWizardOpen(true)}
          onGenerateForDates={() => setWizardOpen(true)}
          onGenerateItem={handleGenerateItem}
        />
      )}

      {view === 'feed' && <KeywordFeedPanel search={search} />}

      <BulkActionBar
        selectedIds={selectedIds}
        onClear={() => setSelectedIds([])}
        onPublish={handleBulkPublish}
        onDraft={handleBulkDraft}
        onSchedule={() => openScheduleDialog(schedulableItems, 'bulk')}
        onRetry={handleBulkRetry}
        onDelete={() => deleteItems(selectedItems)}
        publishCount={publishableItems.length}
        draftCount={draftableItems.length}
        scheduleCount={schedulableItems.length}
        retryCount={failedItems.length}
        isBusy={busyIds.size > 0}
      />

      {processingCount > 0 && (
        <div className="fixed bottom-4 left-4 z-50 flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm text-white shadow-lg">
          <div className="h-2 w-2 animate-pulse rounded-full bg-white" />
          <span>
            Publishing {processingCount}{' '}
            {processingCount === 1 ? 'article' : 'articles'}...
          </span>
        </div>
      )}

      {scheduleDialogOpen && (
        <ScheduleModal
          items={scheduleDialog.items}
          onClose={closeScheduleDialog}
          onConfirm={({ date, days, timeOfDay }) => {
            const dialog: ScheduleDialogState = {
              ...scheduleDialog,
              selectedDate: dayjs(date),
              numDays: days,
              timeOfDay,
            };
            closeScheduleDialog();
            void confirmSchedule(dialog);
          }}
        />
      )}

      <ContentCreationWizard
        open={wizardOpen}
        onOpenChange={(o) => {
          setWizardOpen(o);
          if (!o) setWizardMode(null);
        }}
        initialMode={wizardMode}
      />
    </div>
  );
};

export default ContentPlannerPage;
